import { useEffect, useRef, useState } from 'react';
import { portalApiGet } from '@/lib/portal-api';
import AcButton from '@/dashboard/ui/AcButton';
import DoctorCoverResults, {
  type CoverResults,
  type CoverResultRow,
} from '@/walkin/DoctorCoverResults';
import { uncoveredCoverIntervals } from '@/walkin/doctor-cover-gaps';
import { formatIstTime } from '@/lib/datetime-ist';
import {
  formatCoverageDate,
  type CoverageDay,
  type CoverageReceptionReport,
} from './doctor-coverage-view';
import './doctor-cover-followup.css';
export function coverFollowupRows(data: CoverResults): CoverResultRow[] {
  return data.bookings.filter(
    (r) =>
      r.contactOwner === 'coc' ||
      (r.contactOwner === 'receptionist' &&
        r.eligible &&
        ['no_cover', 'outside_cover', 'capacity'].includes(r.outcome)),
  );
}
export default function DoctorCoverFollowup({
  day,
  clinicName,
  onArrange,
  onChanged,
}: {
  day: CoverageDay;
  clinicName: string;
  onArrange: (report: CoverageReceptionReport, trigger: HTMLElement) => void;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<CoverResults[] | null>(null),
    [error, setError] = useState(false),
    [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState<{ reportId: string; appointmentId: number } | null>(
    null,
  );
  const epoch = useRef(0),
    trigger = useRef<HTMLElement | null>(null);
  const reports = day.receptionReports ?? [],
    key = reports
      .map((r) => r.reportId)
      .sort()
      .join(',');
  // A saved conversation refreshes the list without unmounting its continuation.
  useEffect(() => {
    setSelected(null);
  }, [day.clinicId, day.date, key]);
  useEffect(() => {
    const token = ++epoch.current;
    setRows(null);
    setError(false);
    void Promise.all(
      reports.map((r) =>
        portalApiGet<CoverResults>(
          `/reception/doctor-absence-reports/${r.reportId}/affected-bookings?clinicId=${day.clinicId}`,
        ),
      ),
    )
      .then((result) => {
        if (token === epoch.current) setRows(result);
      })
      .catch(() => {
        if (token === epoch.current) {
          setRows(null);
          setSelected(null);
          setError(true);
        }
      });
    return () => {
      epoch.current++;
    };
  }, [day.clinicId, day.date, key, day.assignments, revision]);
  const close = () => {
    setSelected(null);
    requestAnimationFrame(() => trigger.current?.isConnected && trigger.current.focus());
  };
  return (
    <section className="coc-cover-followup" aria-label={`${clinicName} doctor cover`}>
      {error ? (
        <p role="alert">
          Doctor cover unavailable.{' '}
          <AcButton onClick={() => setRevision((n) => n + 1)}>Retry cover</AcButton>
        </p>
      ) : rows === null ? (
        <p role="status">Loading doctor cover…</p>
      ) : (
        rows.map((data) => {
          const report = reports.find((r) => r.reportId === data.report.report_id);
          if (!report) return null;
          const followup = coverFollowupRows(data),
            confirmed = data.coordination.filter((c) => c.status === 'confirmed');
          const gaps = uncoveredCoverIntervals(
            data.decision?.absenceHours?.startsAt ?? data.report.starts_at,
            data.decision?.absenceHours?.endsAt ?? data.report.ends_at,
            confirmed,
          );
          const startsAt = data.decision?.absenceHours?.startsAt ?? data.report.starts_at;
          const endsAt = data.decision?.absenceHours?.endsAt ?? data.report.ends_at;
          const hours =
            startsAt && endsAt
              ? `${formatIstTime(startsAt)}–${formatIstTime(endsAt)} IST`
              : 'Hours need confirmation';
          const names = [
            ...new Set(
              confirmed.map(
                (c) =>
                  day.doctors.find((d) => d.legacyDoctorId === c.legacy_doctor_id)?.name ??
                  'Doctor name unavailable',
              ),
            ),
          ];
          const calls = data.bookings.filter(
            (r) =>
              r.contactOwner === 'coc' &&
              !['informed', 'agreed'].includes(r.contact?.outcome ?? ''),
          ).length;
          const inPerson = data.bookings.filter(
            (r) =>
              r.contactOwner === 'receptionist' &&
              !['informed', 'agreed'].includes(r.contact?.outcome ?? ''),
          ).length;
          return (
            <div key={report.reportId} className="coc-cover-followup__report">
              <section className="coc-cover-followup__card">
                <div className="coc-cover-followup__top">
                  <span className="coc-cover-followup__initial" aria-hidden="true">
                    {report.doctorName
                      .replace(/^Dr\.?\s*/i, '')
                      .slice(0, 1)
                      .toUpperCase()}
                  </span>
                  <div>
                    <h3>{report.doctorName} is unavailable</h3>
                    <p>
                      {clinicName} · {formatCoverageDate(report.date)} · {hours}
                    </p>
                  </div>
                  <AcButton
                    variant={data.decision ? 'secondary' : 'primary'}
                    onClick={(e) => onArrange(report, e.currentTarget)}
                  >
                    {data.decision ? 'View details' : 'Arrange cover'}
                  </AcButton>
                </div>
                <div className="coc-cover-followup__bottom">
                  {data.decision?.state === 'confirmed' ? (
                    <>
                      <strong>{names.join(' + ')}</strong> covering · {data.counts.affected}{' '}
                      affected
                    </>
                  ) : (
                    <>
                      <strong>{data.counts.affected} bookings affected</strong> · Reception report
                    </>
                  )}
                </div>
                {data.decision?.state === 'confirmed' && gaps && gaps.length > 0 && (
                  <div className="coc-cover-followup__gap">
                    <strong>
                      {gaps
                        .map(
                          ([a, b]) =>
                            `${formatIstTime(new Date(a).toISOString())}–${formatIstTime(new Date(b).toISOString())}`,
                        )
                        .join(' · ')}{' '}
                      uncovered
                    </strong>
                    <small>COC will arrange cover or agree a new time.</small>
                  </div>
                )}
                {data.decision?.state === 'no_cover' && (
                  <div className="coc-cover-followup__gap">
                    <strong>No replacement confirmed</strong>
                    <small>
                      Keep bookings until a new time is agreed. Reception tells patients at the
                      clinic.
                    </small>
                  </div>
                )}
              </section>
              {data.decision ? (
                <section className="coc-cover-followup__card">
                  <div className="coc-cover-followup__heading">
                    <div>
                      <h3>Patient follow-up</h3>
                      <p>
                        {calls} to contact · {inPerson} in-person updates with Reception
                      </p>
                    </div>
                    <span>{followup.length} with you</span>
                  </div>
                  <div className="coc-cover-followup__scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>Patient / appointment</th>
                          <th>Next step</th>
                          <th>Contact</th>
                          <th>
                            <span className="sr-only">Action</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {followup.map((r) => (
                          <tr key={r.appointmentId}>
                            <td>
                              <strong>{r.patientName ?? 'Patient name unavailable'}</strong>
                              <small>
                                {formatIstTime(r.expected.appointmentTime)} ·{' '}
                                {r.doctorName ?? 'Doctor name unavailable'}
                                {r.rebookedFrom
                                  ? ` · ${formatCoverageDate(new Intl.DateTimeFormat('en-CA', { timeZone: day.timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(r.expected.appointmentTime)))}`
                                  : ''}
                              </small>
                            </td>
                            <td>
                              {r.outcome === 'moved'
                                ? 'Tell patient: same time'
                                : r.outcome === 'followup_review'
                                  ? 'Review follow-up'
                                  : ['no_cover', 'outside_cover', 'capacity'].includes(r.outcome)
                                    ? 'Agree a new time'
                                    : 'Tell patient the saved change'}
                            </td>
                            <td>
                              {['informed', 'agreed'].includes(r.contact?.outcome ?? '')
                                ? r.contact?.outcome === 'agreed'
                                  ? 'Agreed'
                                  : 'Told'
                                : r.contactOwner === 'receptionist'
                                  ? 'Tell in person'
                                  : 'COC to contact'}
                              <small>
                                {r.contactOwner === 'receptionist'
                                  ? 'Reception · at clinic'
                                  : r.contact?.outcome === 'unreachable'
                                    ? 'Could not reach · retry'
                                    : 'Phone'}
                              </small>
                            </td>
                            <td>
                              <AcButton
                                ref={(node) => {
                                  if (
                                    node &&
                                    selected?.reportId === report.reportId &&
                                    selected?.appointmentId === r.appointmentId
                                  )
                                    trigger.current = node;
                                }}
                                aria-label={`${r.contactOwner === 'receptionist' ? 'Rebook' : 'Open'} ${r.patientName ?? 'appointment'}`}
                                onClick={(e) => {
                                  trigger.current = e.currentTarget;
                                  setSelected({
                                    reportId: report.reportId,
                                    appointmentId: r.appointmentId,
                                  });
                                }}
                              >
                                {r.contactOwner === 'receptionist' ? 'Rebook' : 'Open'}
                              </AcButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {followup.length === 0 && (
                    <p className="coc-cover-followup__empty">
                      No patients need COC follow-up for this report.
                    </p>
                  )}
                </section>
              ) : (
                <p className="coc-cover-followup__next">
                  <strong>Next: confirm a replacement.</strong> Eligible bookings then move at the
                  same time. COC contacts patients; Reception tells those at the clinic.
                </p>
              )}
            </div>
          );
        })
      )}
      {selected && (
        <DoctorCoverResults
          key={`${selected.reportId}:${selected.appointmentId}`}
          clinicId={day.clinicId}
          clinicName={clinicName}
          reportId={selected.reportId}
          initialAppointmentId={selected.appointmentId}
          role="coc"
          doctors={day.doctors.map((d) => ({ id: d.legacyDoctorId, name: d.name }))}
          onClose={close}
          onChanged={() => {
            onChanged();
            setRevision((n) => n + 1);
          }}
        />
      )}
    </section>
  );
}
